import { z } from 'zod';

/**
 * A single instance of a back-to-back repeat
 */
export const RepeatInstanceSchema = z.object({
  /** The tokens that are repeated (space-separated) */
  repeatedTokens: z.string(),
  /** Token index where the repeat starts */
  startTokenIndex: z.number().int(),
  /** Number of consecutive times the pattern appears (minimum 2) */
  repeatCount: z.number().int(),
  /** Length of the repeated pattern in tokens */
  patternLength: z.number().int(),
});

export type RepeatInstance = z.infer<typeof RepeatInstanceSchema>;

/**
 * Configuration schema for the Z Text Repeat Evaluator
 */
export const ZTextRepeatEvaluatorConfigSchema = z.object({
  variant: z.literal('z_text_repeat').default('z_text_repeat'),
  /** List of characters to use as token boundaries */
  splitOn: z.array(z.string()).default([',', '!', '.', '-', ':', '?', ' ']),
  /** Minimum pattern length (in tokens) to detect */
  minPatternLength: z.number().int().default(1),
});

/**
 * Output schema for the Z Text Repeat Evaluator
 */
export const ZTextRepeatEvaluatorOutputSchema = z
  .object({
    variant: z.literal('z_text_repeat').default('z_text_repeat'),
    /** List of all detected repeat instances */
    repeats: z.array(RepeatInstanceSchema),
    /** Total number of tokens involved in repeats */
    totalRepeatedTokens: z.number().int(),
    /** Total number of tokens in the input text */
    textLengthTokens: z.number().int(),
    /** Maximum repeat count among all patterns (highest repeatCount value) */
    maxRepeatCount: z.number().int(),
  })
  .nullable();

export type ZTextRepeatEvaluatorInput = { text: string };

export type ZTextRepeatEvaluatorConfig = z.infer<
  typeof ZTextRepeatEvaluatorConfigSchema
>;

export type ZTextRepeatEvaluatorOutput = z.infer<
  typeof ZTextRepeatEvaluatorOutputSchema
>;

/**
 * Z Text Repeat Evaluator
 *
 * This evaluator detects back-to-back (consecutive) token repeats anywhere in text
 * using the Z-algorithm. It identifies patterns like:
 * - Single word repeats: "the the"
 * - Multi-word phrase repeats: "I said I said"
 * - Longer repeats: "hello world hello world"
 *
 * The evaluator uses the Z-algorithm to efficiently find all consecutive repeated
 * token sequences. Tokens are words split by configurable delimiters (punctuation, spaces, etc.).
 */
export class ZTextRepeatEvaluator {
  private static readonly DEFAULT_SPLIT_CHARS = [
    ',',
    '!',
    '.',
    '-',
    ':',
    '?',
    ' ',
  ];
  private static readonly DEFAULT_MIN_PATTERN_LENGTH = 1;

  private readonly config: ZTextRepeatEvaluatorConfig;
  private readonly splitPattern: RegExp;

  constructor(
    config: ZTextRepeatEvaluatorConfig = {
      variant: 'z_text_repeat',
      splitOn: ZTextRepeatEvaluator.DEFAULT_SPLIT_CHARS,
      minPatternLength: ZTextRepeatEvaluator.DEFAULT_MIN_PATTERN_LENGTH,
    },
  ) {
    this.config = ZTextRepeatEvaluatorConfigSchema.parse(config);
    this.splitPattern = this.createSplitPattern(this.config.splitOn);
  }

  /**
   * Create a regex pattern for splitting text based on delimiters
   */
  private createSplitPattern(splitChars: string[]): RegExp {
    const escapedChars = splitChars.map((char) =>
      char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
    );
    return new RegExp(`[${escapedChars.join('')}]`);
  }

  /**
   * Process raw text into cleaned tokens
   */
  private processTokens(text: string): string[] {
    return text
      .split(this.splitPattern)
      .map((token) => token.trim())
      .filter((token) => token !== '');
  }

  /**
   * Compute the Z-array for token list.
   *
   * The Z-array z[i] represents the length of the longest token sequence starting
   * at tokens[i] that matches a prefix of tokens.
   *
   * @param tokens - Input token list
   * @returns Z-array where z[i] is the length of longest prefix match at position i
   */
  private zArray(tokens: string[]): number[] {
    const length = tokens.length;
    const zValues = new Array<number>(length).fill(0);
    let leftBound = 0;
    let rightBound = 0;

    for (let index = 1; index < length; index++) {
      if (index <= rightBound) {
        const leftOffset = index - leftBound;
        const previousZ = zValues[leftOffset];
        if (previousZ !== undefined) {
          zValues[index] = Math.min(rightBound - index + 1, previousZ);
        }
      }

      let currentZ = zValues[index];
      currentZ ??= 0;

      while (
        index + currentZ < length &&
        tokens[currentZ] === tokens[index + currentZ]
      ) {
        currentZ++;
      }

      zValues[index] = currentZ;

      if (index + currentZ - 1 > rightBound) {
        leftBound = index;
        rightBound = index + currentZ - 1;
      }
    }

    return zValues;
  }

  /**
   * Find all back-to-back repeats using the Z-algorithm.
   *
   * @param tokens - Token list to analyze
   * @returns List of detected repeat instances
   */
  private findBackToBackRepeats(tokens: string[]): RepeatInstance[] {
    const numTokens = tokens.length;
    const results: RepeatInstance[] = [];
    // Track token indices that are already covered by detected repeats
    const coveredTokens = new Set<number>();

    for (let startIndex = 0; startIndex < numTokens; startIndex++) {
      // Skip if this token is already part of a detected repeat
      if (coveredTokens.has(startIndex)) {
        continue;
      }

      const zValues = this.zArray(tokens.slice(startIndex));

      for (let zIndex = 0; zIndex < zValues.length; zIndex++) {
        const zValue = zValues[zIndex];

        // Skip if zValue is undefined
        if (zValue === undefined) {
          continue;
        }

        // zIndex represents the pattern length in Z-algorithm
        const patternLen = zIndex;

        // If z_value >= pattern_len and pattern_len > 0, we have a back-to-back repeat
        // Pattern: tokens[start_index:start_index+pattern_len] repeats at
        // tokens[start_index+pattern_len:start_index+pattern_len+pattern_len], etc.
        if (
          zValue >= patternLen &&
          patternLen > 0 &&
          patternLen >= this.config.minPatternLength
        ) {
          const repeatCount = Math.floor(zValue / patternLen) + 1;

          // Calculate the range of tokens covered by this repeat
          const endIndex = startIndex + patternLen * repeatCount;

          // Check if any token in this range is already covered
          let hasOverlap = false;
          for (let i = startIndex; i < endIndex; i++) {
            if (coveredTokens.has(i)) {
              hasOverlap = true;
              break;
            }
          }

          if (hasOverlap) {
            continue;
          }

          // Mark all tokens in this repeat as covered
          for (let i = startIndex; i < endIndex; i++) {
            coveredTokens.add(i);
          }

          // Extract the pattern tokens
          const patternTokens = tokens.slice(
            startIndex,
            startIndex + patternLen,
          );
          const patternStr = patternTokens.join(' ');

          results.push({
            repeatedTokens: patternStr,
            startTokenIndex: startIndex,
            repeatCount,
            patternLength: patternLen,
          });

          // Break after finding the first (longest) repeat starting at this position
          break;
        }
      }
    }

    return results;
  }

  /**
   * Analyze text for back-to-back token repeats using the Z-algorithm.
   *
   * @param input - Contains the text to analyze
   * @returns ZTextRepeatEvaluatorOutput containing all detected repeats and metrics, or null for empty input
   */
  public evaluate(
    input: ZTextRepeatEvaluatorInput,
  ): ZTextRepeatEvaluatorOutput {
    if (!input?.text?.trim()) {
      return null;
    }

    // Split into tokens
    const tokens = this.processTokens(input.text);

    // Find all repeats
    const repeats = this.findBackToBackRepeats(tokens);

    // Calculate total repeated tokens
    const totalRepeatedTokens = repeats.reduce(
      (sum, repeat) => sum + repeat.patternLength * repeat.repeatCount,
      0,
    );

    // Find maximum repeat count
    const maxRepeatCount =
      repeats.length > 0 ? Math.max(...repeats.map((r) => r.repeatCount)) : 0;

    return {
      variant: 'z_text_repeat',
      repeats,
      totalRepeatedTokens,
      textLengthTokens: tokens.length,
      maxRepeatCount,
    };
  }

  /**
   * Calculate the proportion of tokens involved in repeats.
   *
   * @param output - The evaluation output
   * @returns A float between 0.0 and 1.0 representing the density of repeats
   */
  public static getRepeatDensity(
    output: NonNullable<ZTextRepeatEvaluatorOutput>,
  ): number {
    if (output.textLengthTokens === 0) {
      return 0.0;
    }
    return output.totalRepeatedTokens / output.textLengthTokens;
  }

  /**
   * Create a new instance of the Z Text Repeat Evaluator
   *
   * @param config - Configuration for the evaluator
   * @returns A configured instance of ZTextRepeatEvaluator
   */
  static create(
    config?: Partial<ZTextRepeatEvaluatorConfig>,
  ): ZTextRepeatEvaluator {
    const fullConfig: ZTextRepeatEvaluatorConfig = {
      variant: 'z_text_repeat',
      splitOn: ZTextRepeatEvaluator.DEFAULT_SPLIT_CHARS,
      minPatternLength: ZTextRepeatEvaluator.DEFAULT_MIN_PATTERN_LENGTH,
      ...config,
    };

    return new ZTextRepeatEvaluator(fullConfig);
  }
}
